/**
 * Settings → Route Migration routing map - list keys only.
 *
 * PRODUCT-378: the three Route Studio verb destinations that make up
 * the route-migration lifecycle - Import, Improve, Go live - moved out
 * of the Route Studio strip into their own Settings page. Their
 * sub-maps came over verbatim from `../route-studio.tsx` (which keeps
 * Overview only); the page registry
 * (`src/modules/route-studio/routing/registry.tsx`) still declares
 * every page once and an `AssertKeysInMap` guard there fails
 * `ts:check` if a registry key has no matching key in EITHER map.
 *
 * Detail (`/:id`) and create (`/new`) paths are composed from the list
 * path by the registry assembler. Improve carries two deeper surfaces
 * of its own: the Compare screen (a shareable read-only URL, SPEC 2.2)
 * and the per-area scenario detail. The old `/app/route-studio/...`
 * spellings of every path here redirect (see the module's
 * `routing/redirects.tsx`).
 */
import React from 'react'
import { T } from '@transifex/react'

import { pathJoin } from 'src/reducers/routing/utils'

export const createRouteMigrationRoutesMap = (prefix: string) => ({
  // "Get routes in" - import cases, with the legacy single-file
  // batch history folded in as a History filter (SPEC 2.2).
  import: pathJoin(prefix)('import').chainMapAuto(
    (importPrefix) => ({
      history: pathJoin(importPrefix)('history').chainMapAuto(
        (historyPrefix) => ({
          ':id': (id = ':id') =>
            pathJoin(historyPrefix)(id).chainWithTitle({
              title: <T _str="Import record" />,
              breadCrumbTitle: <T _str="Import record" />,
            }),
        }),
        {
          title: <T _str="Import history" />,
          breadCrumbTitle: <T _str="History" />,
        }
      ),
      ':id': (id = ':id') =>
        pathJoin(importPrefix)(id).chainWithTitle({
          title: <T _str="Import case" />,
          breadCrumbTitle: <T _str="Case" />,
        }),
    }),
    {
      title: <T _str="Import" />,
      breadCrumbTitle: <T _str="Import" />,
    }
  ),
  // "Improve it" - the areas list is the landing; the area workspace
  // is the detail; scenarios and the Compare screen live only here.
  improve: pathJoin(prefix)('improve').chainMapAuto(
    (improvePrefix) => ({
      compare: pathJoin(improvePrefix)('compare').chainWithTitle({
        title: <T _str="Compare" />,
        breadCrumbTitle: <T _str="Compare" />,
      }),
      ':id': (id = ':id') =>
        pathJoin(improvePrefix)(id).chainMapAuto(
          (areaPrefix) => ({
            scenarios: pathJoin(areaPrefix)('scenarios').chainMapAuto(
              (scenariosPrefix) => ({
                ':id': (scenarioId = ':id') =>
                  pathJoin(scenariosPrefix)(scenarioId).chainWithTitle({
                    title: <T _str="Scenario" />,
                    breadCrumbTitle: <T _str="Scenario" />,
                  }),
              }),
              {
                title: <T _str="Scenarios" />,
                breadCrumbTitle: <T _str="Scenarios" />,
              }
            ),
          }),
          {
            title: <T _str="Area workspace" />,
            breadCrumbTitle: <T _str="Workspace" />,
          }
        ),
    }),
    {
      title: <T _str="Improve" />,
      breadCrumbTitle: <T _str="Improve" />,
    }
  ),
  // "Go live safely" - the one gated door.
  golive: pathJoin(prefix)('golive').chainMapAuto(
    (golivePrefix) => ({
      ':id': (id = ':id') =>
        pathJoin(golivePrefix)(id).chainWithTitle({
          title: <T _str="Go live" />,
          // F4b craft pass: "Go live | Go live" read as a stutter in
          // the breadcrumb; the detail is the record.
          breadCrumbTitle: <T _str="Record" />,
        }),
    }),
    {
      title: <T _str="Go live" />,
      breadCrumbTitle: <T _str="Go live" />,
    }
  ),
})
